import type { QuestionType } from '~/model/QuestionType';
import { VpIcon, VpLink } from '@vtmn-play/react/headless';
import htmlReactParser from 'html-react-parser';
import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import { fetchCollection } from '@/app/actions/server';
import { Discussion } from '@/app/components/Discussion/Discussion';
import { Loader } from '@/app/components/Loader';
import { useAuthenticatedContext } from '@/app/components/Provider';
import { Translate } from '@/app/components/Translate/Translate';
import { checkIfInteractionIsFinished } from '@/app/utils/entities/interactions';
import { getEntityTranslationVersion, getLangAttributeValue } from '@/app/utils/entities/translation';
import { getIdFromUri } from '@/app/utils/entities/uri';
import { Author } from '~/components/commons/Author/Author';
import { VideoInsert } from '~/components/commons/VideoInsert/VideoInsert';
import { ComponentTranslationStatus } from '~/const/appConst';
import { AcceptLanguage } from '~/const/locale';
import { isAuthenticatedAsMainUser } from '~/utils/user/user';
import { useProjectInteractionsContext } from '../provider/ProjectInteractionsProvider';
import { AnnouncementCountersDisplay } from './AnnouncementCountersDisplay';
import { AnnouncementFooter } from './AnnouncementFooter';
import { AnnouncementPicture } from './AnnouncementPicture';

export const Announcement = () => {
  const locale = useLocale();
  const t = useTranslations('ANNOUNCEMENT');

  const { user, isAuthenticated } = useAuthenticatedContext();
  const { interaction: announcement } = useProjectInteractionsContext();

  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState<QuestionType>();
  const [interactionTranslated, setInteractionTranslated] = useState(false);

  const isEnTranslationValid
    = announcement?.translations?.en_GB?.status
      === ComponentTranslationStatus.valid;

  const originalLocale = announcement?.original_locale;

  const interactionData = getEntityTranslationVersion({
    entity: announcement,
    entityTranslated: interactionTranslated,
    isEnTranslationValid,
    originalLocale,
    userLanguage: AcceptLanguage[locale],
  });

  const interactionTitle = interactionData?.title;
  const interactionBody = interactionData?.body;
  const isValidTranslated
    = interactionData?.status === ComponentTranslationStatus.valid;
  const interactionVideo = announcement?.video_url;

  const locked = checkIfInteractionIsFinished(announcement);

  const getTranslationInteraction = () => {
    setInteractionTranslated(!interactionTranslated);
  };

  const langValue = getLangAttributeValue(
    originalLocale,
    AcceptLanguage[locale],
    interactionTranslated,
  );

  const getQuestion = useCallback(async () => {
    if (!isAuthenticatedAsMainUser(user, isAuthenticated))
      return;

    setLoading(true);

    const id = getIdFromUri(announcement.sections[0]);

    const questions = await fetchCollection<QuestionType>({
      pathname: `sections/${id}/questions`,
      revalidate: 0,
    });

    setQuestion(questions['hydra:member']?.[0]);

    setLoading(false);
  }, [announcement, user, isAuthenticated]);

  useEffect(() => {
    getQuestion();
  }, [getQuestion]);

  return (
    <article className="announcement">
      <h2
        lang={langValue}
        className="vp-title-l announcement-card__title"
      >
        {interactionTitle}
      </h2>
      <AnnouncementCountersDisplay
        likes={announcement?.reactions_repartition?.like}
        comments={announcement?.contributions_count}
      />
      {interactionVideo && (
        <div className="announcement__video">
          <VideoInsert title={interactionTitle} url={interactionVideo} />
        </div>
      )}
      {announcement.parent && (
        <VpLink
          className="announcement__parent-link"
          href={`/interactions/${announcement.parent.id}`}
        >
          {t('PARENT_LINK')}
          <VpIcon name="chevron-right" />
        </VpLink>
      )}
      <AnnouncementPicture interaction={announcement} />
      <Translate
        getTranslation={getTranslationInteraction}
        originalLanguage={originalLocale}
        userLanguage={AcceptLanguage[locale]}
        translated={interactionTranslated}
        entityUri={announcement['@id']}
        isValidTranslated={isValidTranslated}
        isEnTranslationValid={isEnTranslationValid}
      />
      <div
        lang={langValue}
        className="vp-body-m announcement-card__description"
      >
        {interactionBody && htmlReactParser(interactionBody)}
      </div>
      <div className="announcement__author">
        <Author
          user={announcement.owner}
          beginDate={announcement.start_at}
        />
      </div>
      <AnnouncementFooter interaction={announcement} />
      {!!question && (
        <div
          aria-orientation="horizontal"
          className="vtmn-divider vtmn-divider_orientation--horizontal"
          role="separator"
        />
      )}
      <Loader loading={loading}>
        <Discussion
          interaction={announcement}
          question={question}
          locked={locked}
        />
      </Loader>
    </article>
  );
};
